import { formatBRL, centavosParaReais } from '@pgcustom/shared-utils';
import type { Ops, Comissao, EtapaOps } from '@pgcustom/shared-types';
import type { PgTable, PgTableColumn, PgTableBadgeCell, PgChart } from '@pgcustom/design-system';
import type { Resultado } from '../../core/resultado.js';
import { logger } from '../../core/logger.js';
import type { OpsApi } from '../producao/ops.api.js';
import type { ComissoesApi } from '../comissoes/comissoes.api.js';
import type { SocietarioApi } from '../societario/societario.api.js';

const ETAPA_BADGE: Record<EtapaOps, PgTableBadgeCell> = {
  recebido: { texto: 'Recebido', tom: 'neutro' },
  pintura: { texto: 'Pintura', tom: 'destaque' },
  montagem: { texto: 'Montagem', tom: 'destaque' },
  testes: { texto: 'Testes', tom: 'alerta' },
  embalagem: { texto: 'Embalagem', tom: 'alerta' },
  enviado: { texto: 'Concluído', tom: 'sucesso' },
};

const ETAPA_COR: Record<EtapaOps, string> = {
  recebido: 'var(--pg-color-texto-secundario)',
  pintura: 'var(--pg-color-roxo)',
  montagem: 'var(--pg-color-destaque)',
  testes: 'var(--pg-color-alerta)',
  embalagem: 'var(--pg-color-alerta)',
  enviado: 'var(--pg-color-sucesso)',
};

/** Meses de histórico do gráfico de comissão — recorte client-side de `/comissoes/me` (já buscada por completo), mesmo padrão de `dashboard-colaborador.page.ts`. */
const OPCOES_PERIODO = [3, 6, 12] as const;

const STATUS_BONUS_BADGE: Record<string, PgTableBadgeCell> = {
  proposto: { texto: 'Proposto', tom: 'neutro' },
  aprovado: { texto: 'Aprovado', tom: 'destaque' },
  pago: { texto: 'Pago', tom: 'sucesso' },
  rejeitado: { texto: 'Rejeitado', tom: 'erro' },
  cancelado: { texto: 'Cancelado', tom: 'erro' },
};

/**
 * `<erp-dashboard-tecnico-page>` — visão do Técnico (referência visual
 * "fase de finalização", perfil Pastor Mauro). Vive dentro do
 * `<erp-shell>` existente, mas todo dado é escopado ao PRÓPRIO técnico
 * (`OpsApi.listar({ tecnicoId: meuUsuarioId })`, `/comissoes/me`,
 * `/societario/me`) — nunca a lista administrativa completa.
 *
 * A referência visual mostrava um "Bônus PG de Comissão" com nível
 * (Ouro/Diamante) e uma fórmula automática por qualidade/prazo/avaliação
 * somando um percentual extra — isso é uma regra automática de bônus,
 * EXPRESSAMENTE PROIBIDA (owner: "Bônus PG não é uma regra automática...
 * não implemente nenhuma fórmula automática"). Este painel mostra os
 * bônus REAIS já concedidos administrativamente (`societario.bonus_pg`),
 * nunca uma pontuação calculada.
 */
export class ErpDashboardTecnicoPage extends HTMLElement {
  private readonly root: ShadowRoot;
  private _opsApi: OpsApi | null = null;
  private _comissoesApi: ComissoesApi | null = null;
  private _societarioApi: SocietarioApi | null = null;
  private _usuarioId = '';
  private _nomeUsuario = '';
  private periodoMeses: (typeof OPCOES_PERIODO)[number] = 6;
  private minhasComissoes: Comissao[] = [];
  private minhasOps: Ops[] = [];

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
  }

  set opsApi(valor: OpsApi) {
    this._opsApi = valor;
  }

  set comissoesApi(valor: ComissoesApi) {
    this._comissoesApi = valor;
  }

  set societarioApi(valor: SocietarioApi) {
    this._societarioApi = valor;
  }

  set usuarioId(valor: string) {
    this._usuarioId = valor;
  }

  set nomeUsuario(valor: string) {
    this._nomeUsuario = valor;
  }

  connectedCallback(): void {
    this.render();
    void this.carregarOps();
    void this.carregarComissoes();
    void this.carregarBonus();
  }

  private render(): void {
    this.root.innerHTML = `
      <style>
        :host { display: block; font-family: var(--pg-font-familia-base, system-ui); }
        h1 {
          font-family: var(--pg-font-familia-display, system-ui);
          font-size: var(--pg-font-size-xl, 1.5rem);
          color: var(--pg-color-texto-primario, #f2f3f5);
          margin: 0 0 var(--pg-space-1, 4px);
        }
        h1 span { color: var(--pg-color-roxo, #8b5cf6); }
        .cabecalho { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--pg-space-4, 16px); flex-wrap: wrap; margin-bottom: var(--pg-space-5, 24px); }
        .sub { color: var(--pg-color-texto-secundario, #9aa1ab); font-size: var(--pg-font-size-sm, 0.875rem); margin: 0; }
        .seletor-periodo {
          background: var(--pg-color-bg-elevado, #1a1d21);
          border: var(--pg-border-largura, 1px) solid var(--pg-color-borda, #2a2f36);
          border-radius: var(--pg-radius-md, 10px);
          color: var(--pg-color-texto-primario, #f2f3f5);
          font-family: var(--pg-font-familia-base, system-ui);
          font-size: var(--pg-font-size-sm, 0.875rem);
          padding: var(--pg-space-2, 8px) var(--pg-space-3, 12px);
          cursor: pointer;
        }
        .kpis {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
        }
        .graficos {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: var(--pg-space-4, 16px);
          margin-bottom: var(--pg-space-5, 24px);
        }
        .secoes {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
          gap: var(--pg-space-4, 16px);
        }
        @media (max-width: 900px) { .graficos { grid-template-columns: 1fr; } }
      </style>
      <div class="cabecalho">
        <div>
          <h1><span>Olá</span>, ${escaparHtml(primeiroNome(this._nomeUsuario))}</h1>
          <p class="sub">Seus serviços, comissões e bônus.</p>
        </div>
        <select class="seletor-periodo" id="seletor-periodo" title="Período do gráfico de comissões">
          ${OPCOES_PERIODO.map((m) => `<option value="${m}" ${m === this.periodoMeses ? 'selected' : ''}>Últimos ${m} meses</option>`).join('')}
        </select>
      </div>

      <div class="kpis">
        <pg-kpi-card id="kpi-em-andamento" label="Em andamento" icone="producao" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-concluidos" label="Concluídos" icone="producao" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-comissao-pendente" label="Comissão pendente" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-comissao-paga" label="Comissão paga" icone="dinheiro" estado="carregando"></pg-kpi-card>
        <pg-kpi-card id="kpi-bonus" label="Bônus PG recebido" icone="parceiros" estado="carregando"></pg-kpi-card>
      </div>

      <div class="graficos">
        <pg-card>
          <span slot="header">Evolução da sua comissão</span>
          <div id="secao-grafico-comissoes"><pg-empty-state variant="carregando" titulo="Evolução da sua comissão"></pg-empty-state></div>
        </pg-card>
        <pg-card>
          <span slot="header">Seus serviços por etapa</span>
          <div id="secao-grafico-ops"><pg-empty-state variant="carregando" titulo="Serviços por etapa"></pg-empty-state></div>
        </pg-card>
      </div>

      <div class="secoes">
        <pg-card>
          <span slot="header">Seus serviços</span>
          <div id="secao-ops"><pg-empty-state variant="carregando" titulo="Seus serviços"></pg-empty-state></div>
        </pg-card>
        <pg-card>
          <span slot="header">Suas comissões</span>
          <div id="secao-comissoes"><pg-empty-state variant="carregando" titulo="Suas comissões"></pg-empty-state></div>
        </pg-card>
        <pg-card>
          <span slot="header">Seus bônus PG</span>
          <div id="secao-bonus"><pg-empty-state variant="carregando" titulo="Bônus PG"></pg-empty-state></div>
        </pg-card>
      </div>
    `;

    this.root.querySelector('#seletor-periodo')?.addEventListener('change', (evento) => {
      const valor = Number((evento.target as HTMLSelectElement).value);
      if (!OPCOES_PERIODO.includes(valor as (typeof OPCOES_PERIODO)[number])) return;
      this.periodoMeses = valor as (typeof OPCOES_PERIODO)[number];
      this.renderGraficoComissoes();
    });
  }

  private async carregarOps(): Promise<void> {
    if (!this._opsApi || !this._usuarioId) return;
    try {
      const resultado = await this._opsApi.listar({ tecnicoId: this._usuarioId });
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-em-andamento', resultado);
        this.aplicarEstadoKpi('kpi-concluidos', resultado);
        this.renderSecaoEstado('secao-grafico-ops', 'Serviços por etapa', resultado);
        if (resultado.status === 'vazio') {
          this.definirKpi('kpi-em-andamento', '0');
          this.definirKpi('kpi-concluidos', '0');
          this.renderVazio('secao-ops', 'Seus serviços', 'nenhum serviço atribuído a você ainda');
          this.renderVazio('secao-grafico-ops', 'Serviços por etapa', 'nenhum serviço atribuído a você ainda');
        }
        return;
      }

      this.minhasOps = resultado.dado;
      const emAndamento = resultado.dado.filter((o) => o.etapa !== 'enviado');
      const concluidos = resultado.dado.filter((o) => o.etapa === 'enviado');
      this.definirKpi('kpi-em-andamento', String(emAndamento.length));
      this.definirKpi('kpi-concluidos', String(concluidos.length));
      this.renderGraficoOps();

      const body = this.root.querySelector('#secao-ops');
      if (body) {
        const colunas: PgTableColumn[] = [
          { key: 'etapa', label: 'Etapa' },
          { key: 'prioridade', label: 'Prioridade' },
          { key: 'prazo', label: 'Prazo' },
        ];
        const linhas = ordenarPorEtapaAberta(resultado.dado)
          .slice(0, 10)
          .map((ops: Ops) => ({
            etapa: ETAPA_BADGE[ops.etapa],
            prioridade: ops.prioridade,
            prazo: ops.prazo ? new Date(ops.prazo).toLocaleDateString('pt-BR') : '—',
          }));
        body.innerHTML = '<pg-table></pg-table>';
        const tabela = body.querySelector('pg-table') as PgTable;
        tabela.columns = colunas;
        tabela.rows = linhas;
      }
    } catch (erro) {
      logger.erro('falha ao carregar meus serviços (dashboard técnico)', { erro });
      this.aplicarEstadoKpi('kpi-em-andamento', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-concluidos', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarComissoes(): Promise<void> {
    if (!this._comissoesApi) return;
    try {
      const resultado = await this._comissoesApi.minhas('tecnico');
      if (resultado.status !== 'ok') {
        this.aplicarEstadoKpi('kpi-comissao-pendente', resultado);
        this.aplicarEstadoKpi('kpi-comissao-paga', resultado);
        this.renderSecaoEstado('secao-grafico-comissoes', 'Evolução da sua comissão', resultado);
        if (resultado.status === 'vazio') {
          this.definirKpi('kpi-comissao-pendente', formatBRL(0));
          this.definirKpi('kpi-comissao-paga', formatBRL(0));
          this.renderVazio(
            'secao-comissoes',
            'Suas comissões',
            'nenhuma comissão gerada ainda — verifique se existe uma regra de comissão configurada para você'
          );
          this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão gerada ainda');
        }
        return;
      }

      this.minhasComissoes = resultado.dado;
      const pendente = resultado.dado.filter((c: Comissao) => c.status === 'pendente').reduce((s, c) => s + c.valorCentavos, 0);
      const paga = resultado.dado.filter((c: Comissao) => c.status === 'paga').reduce((s, c) => s + c.valorCentavos, 0);
      this.definirKpi('kpi-comissao-pendente', formatBRL(centavosParaReais(pendente)));
      this.definirKpi('kpi-comissao-paga', formatBRL(centavosParaReais(paga)));
      this.renderGraficoComissoes();

      const body = this.root.querySelector('#secao-comissoes');
      if (body) {
        const colunas: PgTableColumn[] = [
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
          { key: 'criadoEm', label: 'Gerada em' },
        ];
        const linhas = resultado.dado.map((c) => ({
          valor: formatBRL(centavosParaReais(c.valorCentavos)),
          status:
            c.status === 'pendente'
              ? ({ texto: 'Pendente', tom: 'alerta' } satisfies PgTableBadgeCell)
              : c.status === 'paga'
                ? ({ texto: 'Paga', tom: 'sucesso' } satisfies PgTableBadgeCell)
                : ({ texto: 'Estornada', tom: 'erro' } satisfies PgTableBadgeCell),
          criadoEm: new Date(c.criadoEm).toLocaleDateString('pt-BR'),
        }));
        body.innerHTML = '<pg-table></pg-table>';
        const tabela = body.querySelector('pg-table') as PgTable;
        tabela.columns = colunas;
        tabela.rows = linhas;
      }
    } catch (erro) {
      logger.erro('falha ao carregar minhas comissões (dashboard técnico)', { erro });
      this.aplicarEstadoKpi('kpi-comissao-pendente', { status: 'erro', mensagem: 'falha ao carregar' });
      this.aplicarEstadoKpi('kpi-comissao-paga', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  private async carregarBonus(): Promise<void> {
    if (!this._societarioApi) return;
    try {
      const resumo = await this._societarioApi.meuResumo();
      const bonusPago = resumo.bonus.filter((b) => b.status === 'pago').reduce((s, b) => s + b.valorCentavos, 0);
      this.definirKpi('kpi-bonus', formatBRL(centavosParaReais(bonusPago)));

      if (resumo.bonus.length === 0) {
        this.renderVazio('secao-bonus', 'Bônus PG', 'nenhum bônus PG registrado para você ainda — o bônus é sempre uma decisão administrativa, não automática');
        return;
      }
      const body = this.root.querySelector('#secao-bonus');
      if (body) {
        const colunas: PgTableColumn[] = [
          { key: 'competencia', label: 'Competência' },
          { key: 'motivo', label: 'Motivo' },
          { key: 'valor', label: 'Valor', align: 'right' },
          { key: 'status', label: 'Status' },
        ];
        const linhas = resumo.bonus.map((b) => ({
          competencia: b.competencia,
          motivo: b.motivo,
          valor: formatBRL(centavosParaReais(b.valorCentavos)),
          status: STATUS_BONUS_BADGE[b.status] ?? { texto: b.status, tom: 'neutro' as const },
        }));
        body.innerHTML = '<pg-table></pg-table>';
        const tabela = body.querySelector('pg-table') as PgTable;
        tabela.columns = colunas;
        tabela.rows = linhas;
      }
    } catch (erro) {
      logger.erro('falha ao carregar bônus (dashboard técnico)', { erro });
      this.aplicarEstadoKpi('kpi-bonus', { status: 'erro', mensagem: 'falha ao carregar' });
    }
  }

  /** Mesmo padrão de `dashboard-colaborador.page.ts` — recorte client-side de comissões já buscadas, agrupadas por mês. */
  private renderGraficoComissoes(): void {
    const body = this.root.querySelector('#secao-grafico-comissoes');
    if (!body) return;
    if (this.minhasComissoes.length === 0) {
      this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão gerada ainda');
      return;
    }

    const desde = new Date();
    desde.setMonth(desde.getMonth() - (this.periodoMeses - 1));
    desde.setDate(1);
    const porMes = new Map<string, number>();
    for (const c of this.minhasComissoes) {
      const data = new Date(c.criadoEm);
      if (data < desde) continue;
      const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
      porMes.set(chave, (porMes.get(chave) ?? 0) + c.valorCentavos);
    }

    if (porMes.size === 0) {
      this.renderVazio('secao-grafico-comissoes', 'Evolução da sua comissão', 'nenhuma comissão no período selecionado');
      return;
    }

    body.innerHTML = '<pg-chart></pg-chart>';
    const chart = body.querySelector('pg-chart') as PgChart;
    chart.type = 'bar';
    chart.series = [{ key: 'comissao', label: 'Comissão' }];
    chart.formatarValor = (centavos: number) => formatBRL(centavosParaReais(centavos));
    chart.points = [...porMes.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([chave, valor]) => ({ label: formatarRotuloPeriodo(chave), values: { comissao: valor } }));
  }

  /** Donut real com as OPS já buscadas — nenhuma chamada nova. */
  private renderGraficoOps(): void {
    const body = this.root.querySelector('#secao-grafico-ops');
    if (!body) return;
    if (this.minhasOps.length === 0) {
      this.renderVazio('secao-grafico-ops', 'Serviços por etapa', 'nenhum serviço atribuído a você ainda');
      return;
    }

    const porEtapa = new Map<EtapaOps, number>();
    for (const ops of this.minhasOps) {
      porEtapa.set(ops.etapa, (porEtapa.get(ops.etapa) ?? 0) + 1);
    }

    body.innerHTML = '<pg-chart></pg-chart>';
    const chart = body.querySelector('pg-chart') as PgChart;
    chart.type = 'donut';
    chart.series = [{ key: 'valor', label: '' }];
    chart.formatarValor = (valor: number) => String(valor);
    chart.points = [...porEtapa.entries()].map(([etapa, quantidade]) => ({
      label: ETAPA_BADGE[etapa].texto,
      values: { valor: quantidade },
      color: ETAPA_COR[etapa],
    }));
  }

  private renderVazio(id: string, titulo: string, descricao: string): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    body.innerHTML = `<pg-empty-state variant="vazio" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(descricao)}"></pg-empty-state>`;
  }

  private renderSecaoEstado(id: string, titulo: string, resultado: Resultado<unknown>): void {
    const body = this.root.querySelector(`#${id}`);
    if (!body) return;
    const variant = resultado.status === 'sem-permissao' ? 'sem-permissao' : resultado.status === 'erro' ? 'erro' : 'indisponivel';
    body.innerHTML = `<pg-empty-state variant="${variant}" titulo="${escaparHtml(titulo)}" descricao="${escaparHtml(legendaDoResultado(resultado))}"></pg-empty-state>`;
  }

  private definirKpi(id: string, valor: string): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'ok');
    card.setAttribute('valor', valor);
  }

  private aplicarEstadoKpi(id: string, resultado: Resultado<unknown>): void {
    const card = this.root.querySelector(`#${id}`);
    if (!card) return;
    card.setAttribute('estado', 'indisponivel');
    card.setAttribute('dica', legendaDoResultado(resultado));
  }
}

function formatarRotuloPeriodo(periodo: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(periodo);
  if (!m) return periodo;
  const [, ano, mes] = m;
  const data = new Date(Number(ano), Number(mes) - 1, 1);
  return data.toLocaleDateString('pt-BR', { month: 'short', year: 'numeric' }).replace('.', '');
}

function ordenarPorEtapaAberta(ops: Ops[]): Ops[] {
  return [...ops].sort((a, b) => Number(a.etapa === 'enviado') - Number(b.etapa === 'enviado'));
}

function primeiroNome(nomeCompleto: string): string {
  return nomeCompleto.trim().split(/\s+/)[0] ?? '';
}

function legendaDoResultado(resultado: Resultado<unknown>): string {
  switch (resultado.status) {
    case 'indisponivel':
      return resultado.motivo;
    case 'sem-permissao':
      return 'você não tem permissão para ver este dado';
    case 'erro':
      return resultado.mensagem;
    default:
      return 'não disponível';
  }
}

function escaparHtml(valor: string): string {
  const div = document.createElement('div');
  div.textContent = valor;
  return div.innerHTML;
}

customElements.define('erp-dashboard-tecnico-page', ErpDashboardTecnicoPage);

declare global {
  interface HTMLElementTagNameMap {
    'erp-dashboard-tecnico-page': ErpDashboardTecnicoPage;
  }
}
